/**
 * FranchiseDetailPage — `/franchise/franchises/:id`.
 *
 * Renders the per-franchise overview:
 *   - GET /v1/franchise/franchises/:id           → header + identity
 *   - GET /v1/franchise/dashboard/franchise/:id  → KPI tiles (revenue, alerts)
 *   - GET /v1/franchise/agreements/franchise/:id → agreement timeline
 *
 * Lifecycle action buttons (suspend / terminate / unsuspend / activate)
 * surface inline. Clicking calls the matching POST and invalidates the
 * franchise query.
 */
import { type JSX, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowUpRight, Building2, FileText, Wallet } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  activateFranchise,
  getFranchise,
  getFranchiseeDashboard,
  listAgreementsForFranchise,
  suspendFranchise,
  terminateFranchise,
  unsuspendFranchise,
} from '../../lib/api/franchise';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

export function FranchiseDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [actionErr, setActionErr] = useState<string | null>(null);

  const franchise = useQuery({
    queryKey: ['franchise', 'detail', id],
    queryFn: () => getFranchise(id),
    retry: false,
    enabled: id.length > 0,
  });
  const dashboard = useQuery({
    queryKey: ['franchise', 'detail', 'dashboard', id],
    queryFn: () => getFranchiseeDashboard(id),
    retry: false,
    enabled: id.length > 0,
  });
  const agreements = useQuery({
    queryKey: ['franchise', 'detail', 'agreements', id],
    queryFn: () => listAgreementsForFranchise(id),
    retry: false,
    enabled: id.length > 0,
  });

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ['franchise', 'detail', id] });
    void qc.invalidateQueries({ queryKey: ['franchise', 'detail', 'dashboard', id] });
  };

  const suspend = useMutation({
    mutationFn: (r: string) => suspendFranchise(id, r),
    onSuccess: () => {
      setReason('');
      setActionErr(null);
      refresh();
    },
    onError: (e: Error) => setActionErr(e.message),
  });
  const unsuspend = useMutation({
    mutationFn: () => unsuspendFranchise(id),
    onSuccess: () => {
      setActionErr(null);
      refresh();
    },
    onError: (e: Error) => setActionErr(e.message),
  });
  const terminate = useMutation({
    mutationFn: (r: string) => terminateFranchise(id, r),
    onSuccess: () => {
      setReason('');
      setActionErr(null);
      refresh();
    },
    onError: (e: Error) => setActionErr(e.message),
  });
  const activate = useMutation({
    mutationFn: () => activateFranchise(id),
    onSuccess: () => {
      setActionErr(null);
      refresh();
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  if (id.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title="Franchise" />
        <p className="text-sm text-muted">Missing franchise id.</p>
      </div>
    );
  }

  if (franchise.isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title="Franchise" />
        <p className="text-sm text-muted">Loading…</p>
      </div>
    );
  }

  if (franchise.isError) {
    const denied = franchise.error instanceof ApiError &&
      (franchise.error.status === 401 || franchise.error.status === 403);
    return (
      <div className="space-y-6">
        <PageHeader title="Franchise" />
        {denied ? (
          <DeferredNotice title="Permission required">
            The dev token doesn&apos;t carry <code className="font-mono">Franchise/read</code>.
          </DeferredNotice>
        ) : (
          <DeferredNotice title="Service unreachable">
            {(franchise.error as Error).message}
          </DeferredNotice>
        )}
      </div>
    );
  }

  const f = franchise.data;
  const status = f.status;
  const canSuspend = status === 'LIVE';
  const canUnsuspend = status === 'SUSPENDED';
  const canTerminate = status !== 'TERMINATED';
  const canActivate = status === 'TRAINING';

  return (
    <div className="space-y-6">
      <PageHeader
        title={f.legalName}
        description={`${f.franchiseNumber} · ${f.tierCode} · ${f.state} / ${f.lga}`}
        actions={
          <div className="flex items-center gap-2">
            <Link
              to={`/franchise/tiers/${encodeURIComponent(f.id)}/upgrade`}
              className="px-3 py-2 border border-border rounded-lg text-sm font-medium hover:bg-gray-50 inline-flex items-center gap-2"
            >
              Tier change <ArrowUpRight className="w-4 h-4" />
            </Link>
            <StatusBadge status={status} />
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="30-day revenue"
          value={dashboard.isPending ? '—' : `₦${dashboard.data?.last30DayRevenue ?? '0'}`}
          icon={Wallet}
        />
        <KPICard
          title="30-day commission"
          value={dashboard.isPending ? '—' : `₦${dashboard.data?.last30DayCommission ?? '0'}`}
          icon={Wallet}
        />
        <KPICard
          title="Children"
          value={dashboard.isPending ? '—' : String(dashboard.data?.descendantCount ?? 0)}
          icon={Building2}
        />
        <KPICard
          title="Inventory alerts"
          value={dashboard.isPending ? '—' : String(dashboard.data?.openInventoryAlerts ?? 0)}
          icon={FileText}
          tone="warning"
        />
      </div>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-border rounded-xl p-5 space-y-2">
          <h2 className="text-sm font-semibold text-gray-900 mb-2">Identity</h2>
          <DetailRow label="Franchise #" value={f.franchiseNumber} mono />
          <DetailRow label="Trading name" value={f.tradingName ?? '—'} />
          <DetailRow label="Owner customer" value={f.ownerCustomerId} mono />
          <DetailRow label="Parent" value={f.parentId ?? '—'} mono />
          <DetailRow label="Commission" value={`${(Number(f.commissionRate) * 100).toFixed(2)} %`} />
          <DetailRow label="Applied" value={f.appliedAt ? new Date(f.appliedAt).toLocaleString() : '—'} />
          <DetailRow label="Live" value={f.liveAt ? new Date(f.liveAt).toLocaleString() : '—'} />
        </div>

        <div className="bg-white border border-border rounded-xl p-5 space-y-3">
          <h2 className="text-sm font-semibold text-gray-900 mb-2">Lifecycle actions</h2>
          {canSuspend || canTerminate ? (
            <div className="space-y-2">
              <label className="block text-xs font-medium text-muted">Reason</label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => activate.mutate()}
              disabled={!canActivate || activate.isPending}
              className="px-3 py-1.5 text-xs font-semibold border border-border rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Activate
            </button>
            <button
              type="button"
              onClick={() => suspend.mutate(reason.trim())}
              disabled={!canSuspend || reason.trim().length === 0 || suspend.isPending}
              className="px-3 py-1.5 text-xs font-semibold border border-amber-300 text-amber-800 rounded-lg hover:bg-amber-50 disabled:opacity-50"
            >
              Suspend
            </button>
            <button
              type="button"
              onClick={() => unsuspend.mutate()}
              disabled={!canUnsuspend || unsuspend.isPending}
              className="px-3 py-1.5 text-xs font-semibold border border-border rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Unsuspend
            </button>
            <button
              type="button"
              onClick={() => terminate.mutate(reason.trim())}
              disabled={!canTerminate || reason.trim().length === 0 || terminate.isPending}
              className="px-3 py-1.5 text-xs font-semibold border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50"
            >
              Terminate
            </button>
          </div>
          {actionErr ? <p className="text-xs text-danger">{actionErr}</p> : null}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Agreements</h2>
        {agreements.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : agreements.isError ? (
          <DeferredNotice title="Agreements unavailable">
            {(agreements.error as Error).message}
          </DeferredNotice>
        ) : (agreements.data?.length ?? 0) === 0 ? (
          <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
            No agreements on file.
          </p>
        ) : (
          <div className="bg-white border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Number</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Effective</th>
                  <th className="text-left px-4 py-2">Until</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {agreements.data?.map((a) => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs">{a.agreementNumber}</td>
                    <td className="px-4 py-2"><StatusBadge status={a.status} /></td>
                    <td className="px-4 py-2 text-xs text-muted">
                      {new Date(a.effectiveFrom).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted">
                      {new Date(a.effectiveUntil).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function DetailRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex justify-between gap-4 text-xs">
      <span className="text-muted">{label}</span>
      <span className={`text-gray-800 ${mono === true ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}
